import React, { useState } from 'react';
import { Route, Switch, useHistory, useRouteMatch } from 'react-router-dom';
import { useSelector } from 'react-redux';

import compare from '../../../lib/compare';
import usePagination from '../../../lib/hooks/usePagination';
import { useFilters } from '../../../components/common/Filters/Filters';
import { selectStudents } from '../../../redux/reducers/StudentsReducer';
import { selectStudentsResults } from '../../../redux/reducers/StudentsResultsReducer';

import Input from '../../../components/common/ui/Inputs/Input';
import ActionsBar from '../../../components/common/ActionsBar/ActionsBar';
import StudentsFilters from '../../../components/admin/StudentsList/Filters';
import StudentsTable from '../../../components/admin/StudentsList/Table';
import StudentsDiagram from '../../../components/teacher/StudentsDiagram/StudentsDiagram';
import StudentResultsRenderer from '../../../components/teacher/StudentResults/StudentResultsRenderer';

import tableIcon from '../../../assets/table.svg';
import diagramIcon from '../../../assets/diagram.svg';
import downloadIcon from '../../../assets/download_light.svg';
import downloadFile from '../../../lib/downloadFile';
import { apiHost } from '../../../config';
import StudentResults from '../../../types/StudentResults';
import NormsTable from '../../../components/teacher/NormsTable/NormsTable';
import { selectGrades } from '../../../redux/reducers/GradesReducer';

const StudentsPage = () => {
  const match = useRouteMatch();
  const history = useHistory();

  const [recordsToBeDisplayed, setRecordsToBeDisplayed] = useState(10);
  const [filters, setFilter] = useFilters({
    username: '',
    municipality: '',
    city: '',
    schoolType: '',
    school: '',
    sex: '',
    age: '',
    grade: '',
    gradeLetter: '',
    orphan: '',
    visionImpairment: '',
    hearingImpairment: '',
    musculoskeletalImpairment: '',
    doneTesting: '',
    result: '',
  });

  const [sortData, setSortData] = useState({
    column: 'ID',
    descending: false,
  });

  const grades = useSelector(selectGrades);
  const students = useSelector(selectStudents);
  const studentsResults = useSelector(selectStudentsResults);

  const studentsResultsForRender: StudentResults[] = [];

  const filteredAndSortedStudents = Object.values(students)
    .filter((student) => {
      const passed =
        new RegExp(filters.username, 'i').test(student.username) &&
        (filters.municipality === '' ||
          filters.municipality ===
          student.school?.city?.municipality?.id?.toString(10)) &&
        (filters.city === '' ||
          filters.city === student.school?.city?.id?.toString(10)) &&
        (filters.schoolType === '' ||
          filters.schoolType === student.school?.typeId?.toString(10)) &&
        (filters.school === '' ||
          filters.school === student.school?.id?.toString(10)) &&
        new RegExp(filters.sex, 'i').test(student.sex) &&
        new RegExp(filters.age, 'i').test(student.age.toString(10)) &&
        new RegExp(filters.grade, 'i').test(student.grade?.name) &&
        new RegExp(filters.gradeLetter, 'i').test(student.grade?.letter) &&
        new RegExp(filters.orphan, 'i').test(
          (student?.orphan ? 1 : 0).toString(),
        ) &&
        new RegExp(filters.visionImpairment, 'i').test(
          (student?.visionImpairment ? 1 : 0).toString(),
        ) &&
        new RegExp(filters.hearingImpairment, 'i').test(
          (student?.hearingImpairment ? 1 : 0).toString(),
        ) &&
        new RegExp(filters.musculoskeletalImpairment, 'i').test(
          (student?.musculoskeletalImpairment ? 1 : 0).toString(),
        ) &&
        new RegExp(filters.doneTesting, 'i').test(
          (student?.doneTesting ? 1 : 0).toString(),
        ) &&
        new RegExp(filters.result, 'i').test(
          (studentsResults?.[student.id]?.result ?? 0).toString(),
        );

      if (passed && studentsResults[student.id]) {
        studentsResultsForRender.push(studentsResults[student.id]);
      }

      return passed;
    })
    .sort((first, second) => {
      const comparedUsernameLength = compare(
        first.username.length,
        second.username.length,
      );

      switch (sortData.column) {
        case 'ID':
          if (comparedUsernameLength !== 0) return comparedUsernameLength;
          return compare(first.username, second.username);
        case 'Муниципалитет':
          return compare(
            first.school?.city?.municipality?.name ?? '',
            second.school?.city?.municipality?.name ?? '',
          );
        case 'Город':
          return compare(
            first.school?.city?.name ?? '',
            second.school?.city?.name ?? '',
          );
        case 'Школа':
          return compare(first.school?.name ?? '', second.school?.name ?? '');
        case 'Пол':
          return compare(first.sex, second.sex);
        case 'Возраст':
          return compare(first.age, second.age);
        case 'Сирота':
          return compare(first.orphan, second.orphan);
        case 'НЗ':
          return compare(first.visionImpairment, second.visionImpairment);
        case 'НС':
          return compare(first.hearingImpairment, second.hearingImpairment);
        case 'НОДА':
          return compare(first.musculoskeletalImpairment, second.musculoskeletalImpairment);
        case 'Прошёл':
          return compare(first.doneTesting, second.doneTesting);
        case 'КВЕРИПО':
          return compare(
            studentsResults?.[first.id]?.KVERIPO ?? 0,
            studentsResults?.[second.id]?.KVERIPO ?? 0,
          );
        case 'ИРП':
          return compare(
            studentsResults?.[first.id]?.IRP ?? 0,
            studentsResults?.[second.id]?.IRP ?? 0,
          );
        case 'Вероятность проявлений рикового поведения':
          return compare(
            studentsResults?.[first.id]?.result ?? 0,
            studentsResults?.[second.id]?.result ?? 0,
          );
        default:
          return compare(first.username, second.username);
      }
    });

  if (sortData.descending) filteredAndSortedStudents.reverse();

  const { paginator, page } = usePagination(
    Math.ceil(filteredAndSortedStudents.length / recordsToBeDisplayed),
  );

  const studentsForRender = filteredAndSortedStudents.slice(
    (page - 1) * recordsToBeDisplayed,
    page * recordsToBeDisplayed,
  );

  const actionsBar = (
    <ActionsBar
      actionButtons={[
        {
          src: tableIcon,
          alt: 'Список учеников',
          placement: 'center',
          onClick: () => history.push(`${match.url}`),
        },
        {
          src: diagramIcon,
          alt: 'Статистика',
          placement: 'center',
          onClick: () => history.push(`${match.url}/diagram`),
        },
        {
          src: downloadIcon,
          alt: 'Выгрузить в Excel',
          placement: 'right',
          onClick: () =>
            downloadFile(
              `${apiHost}/api/admin/students/xlsx/results?${Object.entries(
                filters,
              )
                .map(([key, value]) => `${key}=${value}`)
                .join('&')}`,
              'Результаты.xlsx',
            ),
        },
      ]}
    />
  );

  const recordsToBeDisplayedInput = (
    <div className="d-flex align-items-center">
      <label htmlFor="records-to-be-displayed-input">
        Кол-во записей на одной странице
      </label>

      <Input
        id="records-to-be-displayed-input"
        style={{
          height: 37,
          width: 70,
          marginLeft: 15,
          textAlign: 'center',
        }}
        value={recordsToBeDisplayed}
        onChange={(value) => setRecordsToBeDisplayed(parseInt(value, 10) || 0)}
      />
    </div>
  );

  return (
    <>
      <Switch>
        <Route path={`${match.url}`} exact>
          <div style={{ display: 'flex', gap: '5rem' }}>
            <div style={{ width: '50vw', minWidth: '20rem' }}>
              <div
                className="d-flex align-items-start"
                style={{ height: '80px' }}
              >
                {recordsToBeDisplayedInput}
                <div className="flex-grow-1">{actionsBar}</div>
              </div>

              <NormsTable />
              <br />

              <StudentsTable
                students={studentsForRender}
                grades={grades}
                studentsResults={studentsResults}
                sortData={sortData}
                setSortData={setSortData}
              />
              {paginator}
            </div>

            <StudentsFilters filters={filters} setFilter={setFilter} />
          </div>
        </Route>

        <Route path={`${match.url}/diagram`} exact>
          <div style={{ display: 'flex', gap: '5rem' }}>
            <div style={{ width: '50vw', minWidth: '20rem' }}>
              <div
                className="d-flex align-items-start"
                style={{ height: '80px' }}
              >
                <div className="flex-grow-1">{actionsBar}</div>
              </div>

              <StudentsDiagram studentsResults={studentsResultsForRender} />
            </div>

            <StudentsFilters filters={filters} setFilter={setFilter} />
          </div>
        </Route>

        <Route path={`${match.url}/:userId`}>
          <StudentResultsRenderer />
        </Route>
      </Switch>
    </>
  );
};

export default StudentsPage;
